import { hits, configure } from "instantsearch.js/es/widgets";
import { getInstance, registerWidget } from "./searchsync-create-instance";
import { renderHitTemplate as fallbackTemplate } from "../../hits-templates/default-template.js";
import { renderManagedCardTemplate } from "./managed-card-template.js";
import { preFilterHits } from './searchsync-pre-filter.js';
import { initFavoritesSlots } from './searchsync-favorites-slot.js';
import { applyFilters, addFilter } from '@wordpress/hooks';
import { __, _n, sprintf } from './i18n.js';

/**
 * Derives the current user's enrollment status string for a hit.
 *
 * userCourseStatuses has the shape: { not_started: [id,...], in_progress: [id,...], completed: [id,...] }
 * objectID format: "sfwd-courses#30"
 *
 * Returns one of: 'completed' | 'in_progress' | 'not_started' | 'not_enrolled' | ''
 */
/**
 * Resolves the `users_course_enrollment_status` datapoint against the user's ENROLLMENT buckets —
 * the same `get_user_enrollment_statuses()` payload the Enrollment Status refinement list filters
 * on, so a card and that filter always agree about a course.
 *
 * This used to read `user_course_statuses`, which reports PROGRESS (not_started / in_progress /
 * completed), and returned a hardcoded 'not_enrolled' for anything not in those buckets. So the
 * attribute reported a vocabulary neither its name nor its own filter implied: `enrolled` could
 * never appear, while `not_started` and `in_progress` could — values the enrollment filter has no
 * checkbox for. Reported 2026-08-03; see includes/utils/user-state-labels.php.
 *
 * `completed` is tested first because it is a subset of `enrolled` (a completed course is still an
 * enrolled one), and the more specific state is what a card should show.
 */
function resolveEnrollmentStatus( hit, userEnrollmentStatuses ) {
  if ( ! userEnrollmentStatuses ) return '';
  const courseId = hitCourseId( hit );
  if ( ! courseId ) return '';
  const toArr = v => Array.isArray( v ) ? v : Object.values( v || {} );
  if ( toArr( userEnrollmentStatuses.completed    ).includes( courseId ) ) return 'completed';
  if ( toArr( userEnrollmentStatuses.enrolled     ).includes( courseId ) ) return 'enrolled';
  if ( toArr( userEnrollmentStatuses.not_enrolled ).includes( courseId ) ) return 'not_enrolled';
  return '';
}

/**
 * `users_course_completion_status` — the user's PROGRESS through this course, resolved against
 * `get_user_course_statuses()`. This is the vocabulary the enrollment datapoint used to report by
 * mistake; it now lives on the attribute whose name means it.
 *
 * A course the user isn't enrolled in appears in no bucket and returns '' (renders nothing), which
 * is honest: a course you haven't started has no completion status. The old code invented
 * 'not_enrolled' here, mixing an enrollment value into a progress vocabulary.
 */
function resolveCourseCompletionStatus( hit, userCourseStatuses ) {
  if ( ! userCourseStatuses ) return '';
  const courseId = hitCourseId( hit );
  if ( ! courseId ) return '';
  const toArr = v => Array.isArray( v ) ? v : Object.values( v || {} );
  if ( toArr( userCourseStatuses.completed   ).includes( courseId ) ) return 'completed';
  if ( toArr( userCourseStatuses.in_progress ).includes( courseId ) ) return 'in_progress';
  if ( toArr( userCourseStatuses.not_started ).includes( courseId ) ) return 'not_started';
  return '';
}

/**
 * `users_favorited_courses` — whether THIS record is one of the user's favorites.
 *
 * Matches on `object_ids`, not `ids`: favorites can span any post type, so only the server knows a
 * favorited id's real objectID string, and this JS must not guess a prefix (the same reasoning
 * searchsync-favorites-filter-component.js documents at length).
 *
 * Truncation is handled rather than ignored. The payload is honestly capped and flagged
 * (Context_Serializer's contract), so when `truncated` is true a miss does NOT prove the record
 * isn't favorited — the id may simply have been cut. Returning '' there renders nothing, instead of
 * confidently printing "Not favorited" at a user who did favorite it.
 */
function resolveFavoriteStatus( hit, favorites ) {
  if ( ! favorites ) return '';
  const objectId = hit.objectID || '';
  if ( ! objectId ) return '';
  const toArr     = v => Array.isArray( v ) ? v : Object.values( v || {} );
  const objectIds = toArr( favorites.object_ids ).map( String );
  if ( objectIds.includes( String( objectId ) ) ) return 'favorited';
  return favorites.truncated ? '' : 'not_favorited';
}

/**
 * Course id out of a hit's objectID (`"{post_type}#{id}"` — see includes/algolia/post-to-record.php).
 * PHP may encode arrays with non-sequential keys as plain JS objects, which is why every caller
 * normalises its buckets to real arrays before calling .includes().
 */
function hitCourseId( hit ) {
  const parts = ( hit.objectID || '' ).split( '#' );
  return parts.length === 2 ? parseInt( parts[ 1 ], 10 ) : 0;
}

function initHitsWidget() {
  const tgblAlgoliaDataJSON = window.atob(tgblSearchsyncDataEncoded);
  const tgblAlgoliaData = jQuery.parseJSON(tgblAlgoliaDataJSON);
  // TODO: Translations
  //const LOCALIZED = tgblAlgoliaData.localized;

  const elements = document.querySelectorAll("[data-filter-type='hits']");
  const elementsArray = Array.from(elements);

  const elementsGroupedByIndex = elementsArray.reduce((acc, element) => {
    const dataIndexValue = element.getAttribute("data-index");
    if (!acc[dataIndexValue]) {
      acc[dataIndexValue] = [];
    }
    acc[dataIndexValue].push(element);
    return acc;
  }, {});

  for (const dataIndexValue in elementsGroupedByIndex) {
    if (elementsGroupedByIndex.hasOwnProperty(dataIndexValue)) {
      const elementsWithSameIndex = elementsGroupedByIndex[dataIndexValue];
      let search = getInstance(dataIndexValue);
      
      elementsWithSameIndex.forEach(async element => {
        
        const widgetID                  = element.getAttribute('data-id');
        const widget                    = tgblAlgoliaData[widgetID];
        const templatePath              = widget['template_path'];
        const cardTemplate              = widget['card_template'];
        const cardTemplateHtml          = widget['card_template_html'];
        const preFilterItemsObject      = widget['pre_filter_items'];
        const facetMappingsByAttribute  = widget['facet_mappings_by_attribute'] || {};
        const userCourseStatuses        = widget['user_course_statuses'] || null;
        const userEnrollmentStatuses    = widget['user_enrollment_statuses'] || null;
        // Favorites ride in the PAGE-WIDE payload, not a per-widget entry: every hits instance on a
        // page filters the same index against the same favorites list, so there is nothing
        // per-instance to look up (same key searchsync-favorites-filter-component.js reads).
        const userFavorites             = tgblAlgoliaData?.user_data?.favorites || null;

        if ( preFilterItemsObject ) {
          let preFilterItems = Object.values( preFilterItemsObject ).flat();

          if ( preFilterItems.length !== 0 ) {
            preFilterHits( preFilterItems, dataIndexValue );
          }
        }

        let renderHitTemplate = fallbackTemplate;
        let isInlineLayout    = false;

        if ( cardTemplateHtml ) {
          const tmp   = document.createElement( 'div' );
          tmp.innerHTML = cardTemplateHtml;
          const root  = tmp.querySelector( '[data-template]' );
          isInlineLayout = root?.dataset?.layout === 'inline';
        }

        if ( cardTemplate && cardTemplateHtml ) {
          // Managed template path: slot-based pre-built template.
          renderHitTemplate = ( hit, { html, component } ) => {
            // Each virtual datapoint is added only when its backing payload is present, so a hit
            // never carries a key that would resolve to an empty value purely because the
            // integration is inactive. Registered in PHP by
            // tangible_searchsync_virtual_datapoints() — keep the two lists in step.
            const augmentedHit = { ...hit };
            if ( userEnrollmentStatuses ) {
              augmentedHit.users_course_enrollment_status = resolveEnrollmentStatus( hit, userEnrollmentStatuses );
            }
            if ( userCourseStatuses ) {
              augmentedHit.users_course_completion_status = resolveCourseCompletionStatus( hit, userCourseStatuses );
            }
            if ( userFavorites ) {
              augmentedHit.users_favorited_courses = resolveFavoriteStatus( hit, userFavorites );
            }
            return renderManagedCardTemplate( augmentedHit, cardTemplate, cardTemplateHtml, html, facetMappingsByAttribute );
          };

        } else if ( templatePath ) {
          // Custom template path (old {hit.field} style).
          // TODO: implement full custom-template support once the managed path is stable.
          try {
            const module = await import(templatePath);
            renderHitTemplate = module.default || module.renderHitTemplate;
          } catch (error) {
            console.error(
              `Error loading template from ${templatePath}:`,
              error,
            );
          }
        }

        let SearchsyncHits = ($, search, element) => {
          search.addWidgets([
            hits({
              container: element,
              templates: {
                item: (hit, { html, component }) => {
                  const template = renderHitTemplate(hit, { html, component, __, _n, sprintf }, widgetID);
                  const event = new CustomEvent("algoliaRenderTemplate", {});
                  window.dispatchEvent(event);

                  // THE way for a theme to take over hit rendering completely: return your own
                  // markup from this filter and the card template is bypassed entirely. This
                  // replaced the old approach of selecting a `.js` file from the card-template
                  // picker, which needed a file naming convention and a dynamic import to do less.
                  //
                  // Legacy name fires first, canonical second so it can override the legacy
                  // result — the convention documented in docs/hooks.md and used by
                  // searchsync-create-instance.js's preselect filters.
                  const legacy = applyFilters(
                    'tangible-algolia-hit-template',
                    template,
                    { hit, html, component, widget }
                  );
                  return applyFilters(
                    'tangible_searchsync_hit_template',
                    legacy,
                    { hit, html, component, widget }
                  );
                },
              },
              cssClasses: {
                list: isInlineLayout
                  ? [ 'tgblAlgolia-grid', 'tgblAlgolia-grid--inline' ]
                  : [ 'tgblAlgolia-grid', 'tgblAlgolia-grid--density-16' ],
                item: [
                  "tgblAlgolia-card tgblAlgolia-card--is-style-elevated tgblAlgolia-card--has-interaction",
                ],
              },
            }),
          ]);
          // Favorites button placement: cards can declare a .tgbl-favorites-slot
          // element; the auto-injected button is mirrored into it after each render.
          initFavoritesSlots(search, element);
          registerWidget(element);
        };
        SearchsyncHits($, search, element);
      });
    }
  }
}
  
export { initHitsWidget }